import { useState, useCallback } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import {
  Loader2, Plus, X, Sparkles, AlertTriangle, Shield, Info,
} from "lucide-react";
import { CollapsibleSection } from "./CollapsibleSection";
import TraceabilityPanel from "./TraceabilityPanel";
import CreditCostTag from "../../CreditCostTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CATEGORIES = [
  { name: "People", causes: [""] },
  { name: "Process", causes: [""] },
  { name: "Equipment", causes: [""] },
  { name: "Materials", causes: [""] },
  { name: "Environment", causes: [""] },
  { name: "Measurement", causes: [""] },
];

const FISHBONE_GENERIC_BLOCKLIST = [
  "quality issue", "performance problem", "improvement needed", "general issue", "various causes",
];

function fishboneValidation(fishbone) {
  const errors = [];
  const effect = (fishbone.effect || "").trim();
  if (!effect) errors.push("Problem statement is required.");
  else if (effect.length < 10) errors.push("Problem statement must be at least 10 characters.");
  else {
    const lower = effect.toLowerCase();
    for (const phrase of FISHBONE_GENERIC_BLOCKLIST) {
      if (lower === phrase) { errors.push(`Problem statement too generic ("${phrase}"). Describe the measurable effect.`); break; }
    }
  }
  const categoriesWithCauses = fishbone.categories.filter(cat => cat.causes.some(c => c.trim().length >= 3));
  if (categoriesWithCauses.length < 2) errors.push("At least two causal categories must be explored to ensure structured analysis.");

  const seen = new Set();
  const validCauses = [];
  let hasDuplicates = false;
  for (const cat of fishbone.categories) {
    for (const cause of cat.causes) {
      const normalized = cause.trim().toLowerCase();
      if (normalized.length >= 3) { if (seen.has(normalized)) hasDuplicates = true; seen.add(normalized); validCauses.push(normalized); }
    }
  }
  if (hasDuplicates) errors.push("Duplicate causes detected across categories.");
  return { valid: errors.length === 0, errors, categoriesUsed: categoriesWithCauses.length, totalValidCauses: validCauses.length };
}

export { DEFAULT_CATEGORIES };

export default function FishboneTab({ projectId, token, initialFishbone, initialExistingFishbone, paretoResult }) {
  const [fishbone, setFishbone] = useState(initialFishbone || { effect: "", categories: DEFAULT_CATEGORIES });
  const [existingFishbone, setExistingFishbone] = useState(initialExistingFishbone || null);
  const [aiSuggestionsApplied, setAiSuggestionsApplied] = useState(false);
  const [metaOpen, setMetaOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const validation = fishboneValidation(fishbone);
  const effect = (fishbone.effect || "").trim();
  const hasEffect = effect.length >= 10;
  const isGeneric = hasEffect && FISHBONE_GENERIC_BLOCKLIST.some(p => effect.toLowerCase() === p);
  const hasMeasurable = hasEffect && /\d/.test(effect);

  const suggestRootCauses = useCallback(async () => {
    if (!fishbone.effect) { toast.error("Please enter the problem/effect first"); return; }
    setGenerating(true);
    try {
      const response = await fetch(`${API}/ai/suggest-root-causes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: fishbone.effect, context: { paretoResult } }),
      });
      if (!response.ok) throw new Error("AI suggestion failed");
      const data = await response.json();
      if (data.categories) {
        setFishbone((prev) => ({ ...prev, categories: data.categories.map((c) => ({ name: c.name, causes: c.causes.length ? c.causes : [""] })) }));
        setAiSuggestionsApplied(true);
        toast.success("AI suggestions applied");
      }
    } catch (error) { toast.error(error.message); }
    finally { setGenerating(false); }
  }, [fishbone.effect, token, paretoResult]);

  const saveFishbone = useCallback(async () => {
    const v = fishboneValidation(fishbone);
    if (!v.valid) { toast.error(v.errors[0] || "Validation failed"); return; }
    setAnalyzing(true);
    try {
      const cleanFishbone = {
        effect: fishbone.effect.trim(),
        categories: fishbone.categories.map((c) => ({ name: c.name, causes: c.causes.filter((cause) => cause.trim().length >= 3).map(s => s.trim()) })),
        version: "v1.0-hardened",
      };
      const response = await fetch(`${API}/projects/${projectId}/fishbone`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(cleanFishbone),
      });
      if (!response.ok) throw new Error("Failed to save");
      const saved = await response.json();
      setExistingFishbone(saved);
      toast.success("Fishbone diagram saved");
    } catch (error) { toast.error(error.message); }
    finally { setAnalyzing(false); }
  }, [projectId, token, fishbone]);

  const addCause = (categoryIndex) => setFishbone((prev) => ({ ...prev, categories: prev.categories.map((cat, i) => i === categoryIndex ? { ...cat, causes: [...cat.causes, ""] } : cat) }));

  const updateCause = (categoryIndex, causeIndex, value) => {
    const trimmed = value.trim().toLowerCase();
    if (trimmed.length >= 3) {
      const isDuplicate = fishbone.categories.some((cat, ci) => cat.causes.some((c, cj) => !(ci === categoryIndex && cj === causeIndex) && c.trim().toLowerCase() === trimmed));
      if (isDuplicate) { toast.error("Duplicate cause detected."); return; }
    }
    setFishbone((prev) => ({ ...prev, categories: prev.categories.map((cat, i) => i === categoryIndex ? { ...cat, causes: cat.causes.map((c, j) => (j === causeIndex ? value : c)) } : cat) }));
  };

  const removeCause = (categoryIndex, causeIndex) => setFishbone((prev) => ({ ...prev, categories: prev.categories.map((cat, i) => i === categoryIndex ? { ...cat, causes: cat.causes.filter((_, j) => j !== causeIndex) } : cat) }));

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="fishbone-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold">Fishbone Diagram (Ishikawa)</h3>
        <Button variant="outline" size="sm" onClick={suggestRootCauses} disabled={generating || !hasEffect || isGeneric} className="gap-2" data-testid="suggest-causes-btn">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI Suggest Hypotheses
          <CreditCostTag cost={4} />
        </Button>
      </div>

      <div className="mb-4 space-y-2">
        <Label>Problem/Effect (Head of the Fish)</Label>
        <Input placeholder='e.g., "Customer complaint rate increased from 2% to 6% in Q4"' value={fishbone.effect} onChange={(e) => setFishbone((prev) => ({ ...prev, effect: e.target.value }))} className={`max-w-xl ${isGeneric ? "border-red-500/50" : ""}`} data-testid="fishbone-effect-input" />
        {effect.length > 0 && effect.length < 10 && <p className="text-xs text-red-400" data-testid="fishbone-effect-error">Problem statement must be at least 10 characters.</p>}
        {isGeneric && <p className="text-xs text-red-400" data-testid="fishbone-effect-generic-error">Problem statement too generic. Describe the measurable effect.</p>}
        {hasEffect && !isGeneric && !hasMeasurable && <p className="text-xs text-amber-400" data-testid="fishbone-effect-hint">Consider adding measurable details for stronger framing.</p>}
      </div>

      <div className="p-3 mb-5 bg-muted/30 border border-border rounded-lg" data-testid="fishbone-governance-banner">
        <div className="flex items-start gap-2"><Shield className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" /><p className="text-xs text-muted-foreground">Fishbone analysis captures hypothesized causes. Items listed here are not validated root causes until supported by statistical evidence in the Analyze phase.</p></div>
      </div>

      {aiSuggestionsApplied && (
        <div className="p-3 mb-5 bg-amber-500/5 border border-amber-500/20 rounded-lg" data-testid="fishbone-ai-disclaimer">
          <div className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" /><div><p className="text-xs font-medium text-amber-400 mb-0.5">AI-generated hypotheses (require validation)</p><p className="text-xs text-muted-foreground">These suggestions are brainstorming aids and require validation.</p></div></div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        {fishbone.categories.map((category, catIndex) => {
          const catHasValidCause = category.causes.some(c => c.trim().length >= 3);
          return (
            <div key={catIndex} className={`p-3 rounded-lg border ${catHasValidCause ? "border-primary/30 bg-primary/5" : "border-border bg-card"}`} data-testid={`fishbone-category-${catIndex}`}>
              <h4 className="font-medium text-sm text-primary mb-3">{category.name}</h4>
              <div className="space-y-2">
                {category.causes.map((cause, causeIndex) => (
                  <div key={causeIndex}>
                    <div className="flex items-center gap-2">
                      <Input value={cause} onChange={(e) => updateCause(catIndex, causeIndex, e.target.value)} placeholder="Add potential cause..." className="text-sm" data-testid={`fishbone-cause-${catIndex}-${causeIndex}`} />
                      {category.causes.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeCause(catIndex, causeIndex)}><X className="w-3 h-3" /></Button>}
                    </div>
                    {cause.trim().length === 0 && <p className="text-[10px] text-muted-foreground mt-0.5 ml-1">Describe a specific condition or mechanism.</p>}
                    {cause.trim().length > 0 && cause.trim().length < 3 && <p className="text-[10px] text-red-400 mt-0.5 ml-1">Cause must be at least 3 characters.</p>}
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={() => addCause(catIndex)} className="w-full text-xs"><Plus className="w-3 h-3 mr-1" /> Add Cause</Button>
              </div>
            </div>
          );
        })}
      </div>

      {!validation.valid && effect.length > 0 && (
        <div className="mb-4 space-y-1" data-testid="fishbone-validation-errors">
          {validation.errors.map((err, i) => <p key={i} className="text-xs text-red-400 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3 shrink-0" /> {err}</p>)}
        </div>
      )}

      <div className="flex items-center gap-4">
        <Button onClick={saveFishbone} disabled={analyzing || !validation.valid} data-testid="save-fishbone-btn">{analyzing ? "Saving..." : "Save Fishbone Diagram"}</Button>
        <span className="text-xs text-muted-foreground">{validation.categoriesUsed} categories used | {validation.totalValidCauses} causes entered</span>
      </div>

      {existingFishbone && (
        <div className="mt-5">
          <CollapsibleSection title="Diagram Metadata" icon={<Info className="w-4 h-4 text-primary" />} open={metaOpen} onToggle={() => setMetaOpen(v => !v)} testId="fishbone-metadata">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm" data-testid="fishbone-metadata-grid">
              <div><span className="text-muted-foreground">Created:</span> <span className="font-mono text-muted-foreground ml-1">{existingFishbone.created_at?.split("T")[0] || "N/A"}</span></div>
              <div><span className="text-muted-foreground">Updated:</span> <span className="font-mono text-muted-foreground ml-1">{existingFishbone.updated_at?.split("T")[0] || "N/A"}</span></div>
              <div><span className="text-muted-foreground">Version:</span> <span className="font-mono text-muted-foreground ml-1">{existingFishbone.version || "v1.0"}</span></div>
            </div>
          </CollapsibleSection>
        </div>
      )}

      <TraceabilityPanel projectId={projectId} token={token} />
    </div>
  );
}
